/**
 * FilterBar Component
 * 
 * Combined search, filter dropdown, and view mode toggle bar.
 * Provides consistent filtering UI across all list pages.
 */

import React from 'react';
import { Search, Grid, List } from 'lucide-react';

export interface FilterOption {
  value: string;
  label: string;
}

export interface FilterBarProps {
  /** Placeholder text for search input */
  searchPlaceholder: string;
  /** Current search value */
  searchValue: string;
  /** Search value change handler */
  onSearchChange: (value: string) => void;
  /** Filter dropdown options */
  filterOptions: FilterOption[];
  /** Current filter value */
  filterValue: string;
  /** Filter value change handler */
  onFilterChange: (value: string) => void;
  /** Current view mode (grid or list) */
  viewMode?: 'grid' | 'list';
  /** View mode change handler */
  onViewModeChange?: (mode: 'grid' | 'list') => void;
  /** Whether to show the view toggle (default: true) */
  showViewToggle?: boolean;
  /** Additional filter controls */
  additionalFilters?: React.ReactNode;
}

export function FilterBar({
  searchPlaceholder,
  searchValue,
  onSearchChange,
  filterOptions,
  filterValue,
  onFilterChange,
  viewMode = 'grid',
  onViewModeChange,
  showViewToggle = true,
  additionalFilters,
}: FilterBarProps) {
  return (
    <div className="bg-white p-4 rounded-lg border border-gray-200">
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <Search className="w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder={searchPlaceholder}
              value={searchValue}
              onChange={(e) => onSearchChange(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          <select
            value={filterValue}
            onChange={(e) => onFilterChange(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            {filterOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          
          {additionalFilters}
        </div>

        {showViewToggle && onViewModeChange && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => onViewModeChange('grid')}
              className={`p-2 rounded-lg transition-colors ${
                viewMode === 'grid' 
                  ? 'bg-blue-100 text-blue-600' 
                  : 'text-gray-400 hover:text-gray-600'
              }`}
              title="Grid view"
            >
              <Grid className="w-4 h-4" />
            </button>
            <button
              onClick={() => onViewModeChange('list')}
              className={`p-2 rounded-lg transition-colors ${
                viewMode === 'list' 
                  ? 'bg-blue-100 text-blue-600' 
                  : 'text-gray-400 hover:text-gray-600'
              }`}
              title="List view"
            >
              <List className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
